import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag, BadgeCheck, FlaskConical, FileText, Calculator, ArrowRight, Gift, Newspaper, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { sizeLabel } from "../lib/variants";
import { useCart } from "../context/CartContext";
import { StarRating } from "../components/StarRating";
import { Seo, siteOrigin } from "../components/Seo";
import { NotifyMe } from "../components/NotifyMe";
import { ProductReviews } from "../components/ProductReviews";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const URL_REGEX = /(https?:\/\/[^\s<>"')\]]+)/g;

const linkify = (text) =>
  String(text).split(URL_REGEX).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={`${i}-${part.slice(0, 40)}`}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#8C7B6B] underline underline-offset-2 hover:text-[#5F544A] break-all transition-colors"
        data-testid="reference-link"
      >
        {part}
      </a>
    ) : (
      part
    )
  );

function StickyBuyBar({ product, qty, onAdd, visible }) {
  useEffect(() => {
    document.body.classList.toggle("has-sticky-cta", visible);
    return () => document.body.classList.remove("has-sticky-cta");
  }, [visible]);
  return (
    <div
      data-testid="sticky-buy-bar"
      aria-hidden={!visible}
      className={`lg:hidden fixed inset-x-0 bottom-0 z-40 bg-[#F7F5F2]/95 backdrop-blur-xl border-t border-[#DDD7CF] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-3 transition-transform duration-300 ${visible ? "translate-y-0" : "translate-y-full pointer-events-none"}`}
    >
      <div className="min-w-0 flex-1">
        <p className="font-product text-xs uppercase tracking-wider text-[#1A1A1A] truncate">{product.name} <span className="normal-case text-[#6E675F]">{product.dosage}</span></p>
        <p className="text-base font-medium text-[#1A1A1A]" data-testid="sticky-buy-price">${(product.price * qty).toFixed(2)} <span className="text-[11px] text-[#6E675F]">CAD{qty > 1 ? ` · ${qty} vials` : ""}</span></p>
      </div>
      <button
        data-testid="sticky-add-to-cart-button"
        onClick={onAdd}
        className="inline-flex items-center justify-center gap-2 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-6 h-12 rounded-none hover:bg-[#75665A] active:scale-[0.98] transition-transform duration-200 shrink-0"
      >
        <ShoppingBag className="w-4 h-4" /> Add to cart
      </button>
    </div>
  );
}

function VariantSelector({ variants, currentId }) {
  const navigate = useNavigate();
  if (variants.length < 2) return null;
  return (
    <div className="mt-6" data-testid="variant-selector">
      <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-2.5">{sizeLabel(variants)}</p>
      <div className="flex flex-wrap gap-2">
        {variants.map((v) => {
          const active = v.id === currentId;
          return (
            <button
              key={v.id}
              type="button"
              data-testid={`variant-option-${v.id}`}
              aria-pressed={active}
              onClick={() => !active && navigate(`/product/${v.id}`, { replace: true })}
              className={`flex flex-col items-start px-4 py-2.5 rounded-lg border transition-colors duration-200 ${
                active
                  ? "bg-[#1A1A1A] text-[#F7F5F2] border-[#1A1A1A]"
                  : "bg-white text-[#1A1A1A] border-[#DDD7CF] hover:border-[#8C7B6B]"
              } ${v.in_stock ? "" : "opacity-60"}`}
            >
              <span className="text-sm font-semibold">{v.dosage}</span>
              <span className={`text-[11px] ${active ? "text-[#F7F5F2]/70" : "text-[#6E675F]"}`}>
                ${v.price.toFixed(2)}{v.in_stock ? "" : " · Sold out"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [family, setFamily] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState(null);
  const [studies, setStudies] = useState([]);
  const [guide, setGuide] = useState(null);
  const [ctaVisible, setCtaVisible] = useState(true);
  const ctaRef = useRef(null);
  const { addItem, tiers } = useCart();

  const loadProduct = useCallback(() => {
    Promise.all([api.get(`/products/${id}`), api.get(`/products/${id}/variants`)])
      .then(([{ data }, { data: fam }]) => { setProduct(data); setFamily(fam); })
      .catch(() => toast.error("Product not found"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { loadProduct(); }, [loadProduct]);

  useEffect(() => {
    api.get("/settings")
      .then(({ data }) => setRewards({ enabled: !!data.rewards_enabled, earnRate: data.rewards_earn_rate || 1 }))
      .catch(() => {});
    api.get("/bulletin?limit=100")
      .then(({ data }) => setStudies(data.filter((p) => p.shop_product_id === id)))
      .catch(() => {});
    api.get(`/guides/for-product/${id}`).then(({ data }) => setGuide(data)).catch(() => setGuide(null));
  }, [id]);

  useEffect(() => {
    const el = ctaRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([entry]) => setCtaVisible(entry.isIntersecting), { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [product?.id, product?.in_stock]);

  const addToCart = () => { addItem(product, qty); toast.success(`${product.name} ${product.dosage} added to cart`); };

  if (loading) {
    return (
      <div className="relative z-10 flex justify-center py-40">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!product) {
    return <p className="relative z-10 text-center text-[#6E675F] py-40">Product not found.</p>;
  }

  const hasLab = !!product.lab_result_url;
  const variants = family?.variants || [product];
  const rating = family?.rating_count ? family : product;

  return (
    <div className={`relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-12 ${product.in_stock && !ctaVisible ? "pb-28 lg:pb-12" : ""}`}>
      <Seo
        title={`${product.name} ${product.dosage}`.trim()}
        description={product.description || undefined}
        path={`/product/${product.id}`}
        image={product.image_url}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Product",
              name: `${product.name} ${product.dosage}`.trim(),
              image: product.image_url?.startsWith("http") ? product.image_url : `${siteOrigin()}${product.image_url}`,
              description: product.description,
              sku: product.id,
              category: product.category,
              brand: { "@type": "Brand", name: "Eternal Biolabs" },
              offers: {
                "@type": "Offer",
                priceCurrency: "CAD",
                price: product.price.toFixed(2),
                availability: product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                url: `${siteOrigin()}/product/${product.id}`,
                itemCondition: "https://schema.org/NewCondition",
                shippingDetails: {
                  "@type": "OfferShippingDetails",
                  shippingDestination: { "@type": "DefinedRegion", addressCountry: "CA" },
                },
              },
              ...(rating.rating_count > 0 && {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: rating.rating_avg,
                  reviewCount: rating.rating_count,
                },
              }),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Shop", item: `${siteOrigin()}/` },
                ...(product.category
                  ? [{ "@type": "ListItem", position: 2, name: product.category, item: `${siteOrigin()}/` }]
                  : []),
                {
                  "@type": "ListItem",
                  position: product.category ? 3 : 2,
                  name: `${product.name} ${product.dosage}`.trim(),
                  item: `${siteOrigin()}/product/${product.id}`,
                },
              ],
            },
          ],
        }}
      />
      <Link to="/shop" data-testid="back-to-shop-link" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] hover:text-[#1A1A1A] transition-colors mb-10">
        <ArrowLeft className="w-4 h-4" /> Back to Shop
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="relative bg-white rounded-lg overflow-hidden aspect-square">
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" data-testid="product-detail-image" />
            <span className="absolute top-4 right-4 inline-flex items-center gap-1 bg-white/90 backdrop-blur text-[#2D6A4F] text-[11px] tracking-[0.12em] uppercase font-semibold px-3 py-1.5 rounded-none border border-[#DDD7CF]" data-testid="product-detail-lab-certified">
              <BadgeCheck className="w-3.5 h-3.5" /> Lab Certified
            </span>
          </div>
          {hasLab ? (
            <Link
              to="/lab-results"
              data-testid="see-lab-results-link"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-[#8C7B6B] text-[#8C7B6B] text-xs tracking-[0.15em] uppercase font-semibold px-6 py-3 rounded-none hover:bg-[#8C7B6B] hover:text-white transition-colors duration-300"
            >
              <FlaskConical className="w-4 h-4" /> View Lab Results
            </Link>
          ) : (
            <p className="mt-4 text-center text-xs tracking-[0.15em] uppercase text-[#6E675F]" data-testid="lab-results-pending">
              Lab results available on request
            </p>
          )}
        </div>

        <div className="flex flex-col justify-center">
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#8C7B6B] mb-3">{product.category}</p>
          <h1 className="font-product text-lg uppercase tracking-wide sm:text-3xl text-[#1A1A1A]" data-testid="product-detail-name">
            {product.name}
          </h1>
          <p className="text-lg text-[#6E675F] mt-2" data-testid="product-detail-dosage">{product.dosage} vial · Lyophilized</p>

          {rating.rating_count > 0 && (
            <div className="mt-3" data-testid="product-detail-rating">
              <StarRating value={rating.rating_avg} showValue count={rating.rating_count} />
            </div>
          )}

          <p className="text-3xl font-medium text-[#1A1A1A] mt-6" data-testid="product-detail-price">
            ${product.price.toFixed(2)} <span className="text-sm text-[#6E675F]">CAD</span>
          </p>

          <VariantSelector variants={variants} currentId={product.id} />

          {rewards?.enabled && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#8A6D2F]" data-testid="product-points-earn">
              <Gift className="w-3.5 h-3.5" />
              Earn ~{Math.round(product.price * qty * rewards.earnRate).toLocaleString()} reward points with this purchase
            </p>
          )}

          {(() => {
            const productTiers = product.volume_tiers?.length ? product.volume_tiers : tiers;
            if (!productTiers?.length) return null;
            return (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="product-volume-tiers">
                {[...productTiers].sort((a, b) => a.min_qty - b.min_qty).map((t) => (
                  <span key={`${t.min_qty}-${t.percent}`} className="inline-flex items-center text-[11px] font-semibold text-[#8A6D2F] bg-[#D4B36A]/10 border border-[#D4B36A]/40 rounded-none px-3 py-1">
                    Buy {t.min_qty}+ · {t.percent}% off
                  </span>
                ))}
              </div>
            );
          })()}
          <p className="text-base leading-relaxed text-[#6E675F] mt-6 max-w-lg whitespace-pre-line">{product.description}</p>

          {product.in_stock ? (
            <>
              <div className="flex items-center gap-4 mt-10" ref={ctaRef}>
                <div className="flex items-center border border-[#DDD7CF] rounded-none">
                  <button data-testid="qty-decrease-button" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-[#8C7B6B] transition-colors" aria-label="Decrease quantity">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-10 text-center font-medium" data-testid="qty-value">{qty}</span>
                  <button data-testid="qty-increase-button" onClick={() => setQty(qty + 1)} className="p-3 hover:text-[#8C7B6B] transition-colors" aria-label="Increase quantity">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button
                  data-testid="add-to-cart-button"
                  onClick={addToCart}
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-8 py-4 rounded-none hover:bg-[#75665A] hover:-translate-y-0.5 transition-transform duration-300"
                >
                  <ShoppingBag className="w-4 h-4" /> Add to Cart
                </button>
              </div>
              {product.stock_quantity > 0 && product.stock_quantity <= 5 && (
                <p className="mt-4 text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B]" data-testid="low-stock-label">
                  Only {product.stock_quantity} left in stock
                </p>
              )}
            </>
          ) : (
            <div className="mt-10">
              <p className="text-sm tracking-[0.2em] uppercase font-semibold text-[#6E675F]" data-testid="out-of-stock-label">Currently out of stock</p>
              <NotifyMe productId={product.id} />
            </div>
          )}

          <Link
            to="/calculator"
            data-testid="calculator-page-link"
            className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] hover:text-[#75665A] transition-colors"
          >
            <Calculator className="w-4 h-4" /> Reconstitution Calculator <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          {guide && (
            <Link
              to={`/guides/${guide.slug}`}
              data-testid="product-guide-link"
              className="mt-5 flex items-center gap-3 border border-[#8C7B6B]/40 bg-[#F3EFE9] rounded-lg px-4 py-3 hover:border-[#8C7B6B] transition-colors"
            >
              <BookOpen className="w-4 h-4 text-[#8C7B6B] shrink-0" />
              <span className="text-sm text-[#1A1A1A] flex-1">Read the <strong>{guide.peptide}</strong> research guide — what it is, research areas, evidence &amp; references</span>
              <ArrowRight className="w-4 h-4 text-[#8C7B6B] shrink-0" />
            </Link>
          )}
        </div>
      </div>

      {/* Detail tabs: description / references */}
      <div className="mt-16 max-w-4xl">
        <Tabs defaultValue="details">
          <TabsList className="mb-6">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="research" data-testid="tab-research">Research & References</TabsTrigger>
          </TabsList>

          <TabsContent value="details">
            <div className="space-y-5 whitespace-pre-line text-base leading-relaxed text-[#6E675F]" data-testid="long-description">
              {product.long_description || product.description || ""}
            </div>
          </TabsContent>

          <TabsContent value="research">
            {product.references && product.references.length > 0 ? (
              <ul className="space-y-3" data-testid="references-list">
                {product.references.map((ref, i) => (
                  <li key={`${i}-${ref.slice(0, 30)}`} className="flex gap-3 text-sm text-[#6E675F]">
                    <FileText className="w-4 h-4 text-[#8C7B6B] shrink-0 mt-0.5" />
                    <span className="whitespace-pre-line">{linkify(ref)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[#6E675F]">References available on request.</p>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Matching published studies */}
      {studies.length > 0 && (
        <div className="mt-16 max-w-4xl" data-testid="product-studies-section">
          <div className="flex items-center gap-3 mb-6">
            <Newspaper className="w-4 h-4 text-[#8C7B6B] shrink-0" />
            <h2 className="font-display text-base sm:text-lg uppercase tracking-wide text-[#1A1A1A]">Research on this peptide</h2>
            <div className="flex-1 h-px bg-[#DDD7CF]" />
            <Link to="/research" data-testid="product-studies-view-all" className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] hover:text-[#75665A] transition-colors shrink-0">
              View all →
            </Link>
          </div>
          <div className="space-y-3">
            {studies.slice(0, 3).map((s) => (
              <div key={s.id} className="border border-[#DDD7CF] rounded-lg bg-white p-5" data-testid={`product-study-${s.id}`}>
                <p className="text-sm font-semibold text-[#1A1A1A] leading-snug">{s.title}</p>
                <p className="text-sm text-[#6E675F] mt-1.5 leading-relaxed">{s.summary}</p>
                <p className="text-xs text-[#6E675F] mt-2">
                  {s.journal}{s.year ? ` · ${s.year}` : ""}
                  {s.source_url && (
                    <a href={s.source_url} target="_blank" rel="noopener noreferrer" data-testid={`product-study-link-${s.id}`}
                      className="text-[#8C7B6B] underline underline-offset-2 ml-2 hover:text-[#75665A] transition-colors">
                      Read the study
                    </a>
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviews */}
      <div className="mt-16 max-w-4xl border-t border-[#DDD7CF] pt-12">
        <ProductReviews productId={product.id} onRatingChange={loadProduct} />
      </div>

      <p className="text-xs text-[#6E675F] mt-16 border-t border-[#DDD7CF] pt-6 max-w-4xl">
        For laboratory research use only. Not for human or veterinary consumption.
      </p>
      {product.in_stock && <StickyBuyBar product={product} qty={qty} onAdd={addToCart} visible={!ctaVisible} />}
    </div>
  );
}
